/**
 * MultiBuy Builder - filter.js
 * Pure client-side filter + sort over an already-fetched product array.
 * Filters: Size, Pattern, Fit, Style, Fabric, Color, Price. Size/Color
 * are read from a matching variant option (e.g. an option literally
 * named "Size"). The rest come from tags - either "Facet:Value" colon
 * tags if a merchant's catalog uses that convention, or plain unprefixed
 * tags matched against TAG_VALUE_MAP below (built from a real storefront
 * tag dump - most catalogs don't use the colon convention at all, they
 * just mix bare descriptive tags like "Regular Fit"/"Linen"/"Blue" in
 * with unrelated navigation/marketing tags like "New Arrival"/"RTW").
 * Nothing here is exhaustive for every possible store: buildFacets()
 * only returns values actually present in the fetched products, so an
 * unmapped tag simply doesn't surface as a filter option rather than
 * breaking anything - extend TAG_VALUE_MAP as new values show up.
 * Sorts: featured, newest, alphabetical, price asc/desc.
 * No DOM access. Attaches to window.MB.filter
 */
(function (window) {
  'use strict';

  var MB = (window.MB = window.MB || {});

  var FACET_KEYS = ['size', 'pattern', 'fit', 'style', 'fabric', 'color'];

  // Lowercased raw tag -> canonical display value. Known typo/duplicate
  // tags (e.g. "Cut Away" vs "Cutaway", "Ecentric White" vs "Eccentric
  // White") collapse to one canonical value so they don't show up as two
  // separate filter options for the same thing. A tag not listed here
  // just isn't treated as a value for that facet - most tags in a real
  // catalog are navigation/marketing noise, not filterable attributes.
  var TAG_VALUE_MAP = {
    color: {
      blue: 'Blue',
      navy: 'Navy',
      black: 'Black',
      white: 'White',
      'off white': 'Off White',
      'eccentric white': 'Eccentric White',
      'ecentric white': 'Eccentric White',
      'beyond white': 'Beyond White',
      grey: 'Grey',
      green: 'Green',
      beige: 'Beige',
      brown: 'Brown',
      pink: 'Pink',
      'teal blue': 'Teal Blue',
      olive: 'Olive',
      red: 'Red',
      purple: 'Purple',
      orange: 'Orange',
      maroon: 'Maroon',
      charcoal: 'Charcoal',
      magenta: 'Magenta',
      yellow: 'Yellow',
      gold: 'Gold',
      peach: 'Peach',
      taupe: 'Taupe',
      mustard: 'Mustard',
      multicolor: 'Multicolor',
      silver: 'Silver',
      violet: 'Violet',
      lavender: 'Lavender',
      candy: 'Candy'
    },
    fabric: {
      cotton: 'Cotton',
      '100% cotton': 'Cotton',
      '100% bci cotton': 'Cotton',
      'cotton blend': 'Cotton Blend',
      linen: 'Linen',
      '100% linen': 'Linen',
      'linen blend': 'Linen Blend',
      satin: 'Satin',
      'satin stretch': 'Satin',
      denim: 'Denim',
      oxford: 'Oxford',
      oxfords: 'Oxford',
      tencel: 'Tencel/Lyocell',
      lyocell: 'Tencel/Lyocell',
      '53% tencel 47% cotton': 'Tencel/Lyocell',
      polyester: 'Polyester',
      poplin: 'Poplin',
      knit: 'Knit',
      knits: 'Knit',
      woven: 'Woven'
    },
    fit: {
      'regular fit': 'Regular Fit',
      'aldeno plus-regular fit': 'Regular Fit',
      'slim fit': 'Slim Fit',
      'boxy fit': 'Boxy Fit',
      'relaxed fit': 'Relaxed Fit',
      'plus size': 'Plus Size'
    },
    pattern: {
      solid: 'Solid',
      stripes: 'Stripes',
      striped: 'Stripes',
      checks: 'Checks',
      checkered: 'Checks',
      printed: 'Printed',
      prints: 'Printed',
      textured: 'Textured'
    },
    style: {
      'button down': 'Button Down',
      cutaway: 'Cutaway',
      'cut away': 'Cutaway',
      'spread collar': 'Spread Collar',
      'mandarin collar': 'Mandarin Collar',
      cuban: 'Cuban Collar',
      'small point collar': 'Small Point Collar',
      'small paint collar': 'Small Point Collar',
      'regular collar': 'Regular Collar',
      'short collar': 'Short Collar',
      'wing collar': 'Wing Collar',
      'small collar': 'Small Collar',
      'full sleeve': 'Full Sleeve',
      'half sleeve shirt': 'Half Sleeve',
      'over shirts': 'Over Shirt'
    }
  };

  function parseFacetTag(tag) {
    var idx = (tag || '').indexOf(':');
    if (idx === -1) return null;
    return {
      prefix: tag.slice(0, idx).trim().toLowerCase(),
      value: tag.slice(idx + 1).trim()
    };
  }

  function valuesFromOptions(product, key) {
    var option = (product.options || []).filter(function (o) {
      return (o.name || '').toLowerCase() === key;
    })[0];
    return option && option.values ? option.values : [];
  }

  function valuesFromTags(product, key) {
    var map = TAG_VALUE_MAP[key];
    return (product.tags || []).reduce(function (acc, tag) {
      var parsed = parseFacetTag(tag);
      if (parsed && parsed.prefix === key && parsed.value) acc.push(parsed.value);
      var canonical = map && map[(tag || '').toString().trim().toLowerCase()];
      if (canonical) acc.push(canonical);
      return acc;
    }, []);
  }

  function facetValues(product, key) {
    return valuesFromOptions(product, key).concat(valuesFromTags(product, key));
  }

  function buildFacets(products) {
    var groups = {};
    FACET_KEYS.forEach(function (key) {
      groups[key] = {};
    });
    var minPrice = Infinity;
    var maxPrice = 0;

    products.forEach(function (p) {
      FACET_KEYS.forEach(function (key) {
        facetValues(p, key).forEach(function (v) {
          groups[key][v] = true;
        });
      });
      if (p.price < minPrice) minPrice = p.price;
      if (p.price > maxPrice) maxPrice = p.price;
    });

    var result = {
      minPrice: minPrice === Infinity ? 0 : minPrice,
      maxPrice: maxPrice
    };
    FACET_KEYS.forEach(function (key) {
      result[key] = Object.keys(groups[key]).sort();
    });
    return result;
  }

  /**
   * filters: {
   *   size/pattern/fit/style/fabric/color: string[],
   *   minPrice: cents, maxPrice: cents
   * }
   */
  function applyFilters(products, filters) {
    filters = filters || {};
    return products.filter(function (p) {
      var passesFacets = FACET_KEYS.every(function (key) {
        var selected = filters[key];
        if (!selected || !selected.length) return true;
        var productValues = facetValues(p, key);
        return selected.some(function (v) {
          return productValues.indexOf(v) !== -1;
        });
      });
      if (!passesFacets) return false;
      if (typeof filters.minPrice === 'number' && p.price < filters.minPrice) return false;
      if (typeof filters.maxPrice === 'number' && p.price > filters.maxPrice) return false;
      return true;
    });
  }

  function sortProducts(products, sortKey) {
    var list = products.slice();
    switch (sortKey) {
      case 'newest':
        return list.sort(function (a, b) {
          return new Date(b.created_at) - new Date(a.created_at);
        });
      case 'alphabetical':
        return list.sort(function (a, b) {
          return a.title.localeCompare(b.title);
        });
      case 'price-asc':
        return list.sort(function (a, b) {
          return a.price - b.price;
        });
      case 'price-desc':
        return list.sort(function (a, b) {
          return b.price - a.price;
        });
      case 'featured':
      default:
        return list;
    }
  }

  // Use variant availability: collection JSON may omit product.available.
  function hasStock(product) {
    return (product.variants || []).some(function (v) { return v.available === true; });
  }

  function hasMissingSizes(product) {
    var options = product.options || [];
    var sizeIndex = options.findIndex(function (o) {
      return (o.name || '').trim().toLowerCase() === 'size';
    });
    if (sizeIndex < 0) return false;
    var variants = product.variants || [];
    var sizes = options[sizeIndex].values || variants.map(function (v) { return v.options[sizeIndex]; });
    return sizes.some(function (size) {
      return !variants.some(function (v) { return v.available === true && v.options[sizeIndex] === size; });
    });
  }

  function availableSizeCount(product) {
    var sizeIndex = (product.options || []).findIndex(function (o) {
      return (o.name || '').trim().toLowerCase() === 'size';
    });
    if (sizeIndex < 0) return 0;
    var sizes = [];
    (product.variants || []).forEach(function (v) {
      var size = (v.options || [])[sizeIndex];
      if (v.available === true && size != null && sizes.indexOf(size) === -1) sizes.push(size);
    });
    return sizes.length;
  }

  function stockOrderedProducts(products) {
    return products.map(function (product, index) {
      return { product: product, index: index, missing: hasMissingSizes(product), sizes: availableSizeCount(product) };
    }).filter(function (entry) { return hasStock(entry.product); })
      .sort(function (a, b) {
        return Number(a.missing) - Number(b.missing) || b.sizes - a.sizes || a.index - b.index;
      }).map(function (entry) { return entry.product; });
  }

  function applyAll(products, query, filters, sortKey) {
    var searched = MB.search ? MB.search.searchProducts(products, query) : products;
    var filtered = applyFilters(searched, filters);
    return sortProducts(filtered, sortKey);
  }

  MB.filter = {
    hasStock: hasStock,
    stockOrderedProducts: stockOrderedProducts,
    facetKeys: FACET_KEYS,
    buildFacets: buildFacets,
    applyFilters: applyFilters,
    sortProducts: sortProducts,
    applyAll: applyAll
  };
})(window);
